import { defineRule, configure } from 'vee-validate';
// eslint-disable-next-line camelcase
import { required, email, min, max, min_value, max_value, between, is_not, numeric, url, confirmed, length } from '@vee-validate/rules';
import { FieldContext, ValidationRule } from '@/core/forms/vee-validate.types';
import dictionary from '@/core/dictionary/dictionary';
import { PostalCodeInformation, SearchAddressResponse } from '@/api';
// import { getDecimalSeparator } from '../dom/dom';
import { isValidPhoneNumber } from 'libphonenumber-js';

const decimalSeperator = '.'; //; getDecimalSeparator();

function isValidIpAddress(ipaddress: string) {  
    return ipaddress && /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ipaddress);  
}  

function isValidGuid(guid: string) {  
    return guid && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(guid);  
}  

const rules: Record<string, ValidationRule> = {
    required: {
        validator: required,
        label: getLabel('Required'),
    },
    'required-checkbox': {
        validator: required,
        label: getLabel('RequiredCheckbox'),
    },
    length: {
        validator: length,
        label: getLabel('Length'),
    },
    email: {
        validator: email,
        label: getLabel('Email'),
    },
    emailOrDomain: {
        validator: (value) => {
            if (typeof value !== 'string') {
                return false;
            }

            if (value.length < 3)
                return false;

            if (value.includes('@'))
                return email(value);
                
            if (!value.startsWith('www') && !value.includes('/') && value.includes('.'))
                return true;
            
            return false;
        },
        label: getLabel('EmailOrDomain'),
    },
    domain: {
        validator: (value) => {
            if (typeof value !== 'string' || !value.length) {
                return false;
            }
            // Matches domains like example.com, alge-fri.dk, sub.domain.co.uk
            return /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/.test(value);
        },
        label: 'Indtast et gyldigt domæne',
    },
    iban: {
        validator: (value) => {
            if (typeof value !== 'string')
                return false;

            const normalized = value.replace(/\s/g, '').toUpperCase();
            if (normalized.length < 15 || normalized.length > 34)
                return false;

            if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(normalized))
                return false;

            // ISO 7064 mod-97: move country code and check digits to the end, letters expand to two digits (A = 10 ... Z = 35).
            const rearranged = normalized.slice(4) + normalized.slice(0, 4);
            let remainder = 0;
            for (const character of rearranged) {
                const code = character.charCodeAt(0);
                remainder = code >= 48 && code <= 57
                    ? (remainder * 10 + (code - 48)) % 97
                    : (remainder * 100 + (code - 55)) % 97;
            }

            return remainder === 1;
        },
        label: 'Indtast et gyldigt IBAN-nummer',
    },
    bic: {
        // Optional - not every country requires a BIC/SWIFT code alongside the IBAN.
        validator: (value) => {
            if (typeof value !== 'string' || !value.length)
                return true;

            return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.replace(/\s/g, '').toUpperCase());
        },
        label: 'Indtast en gyldig BIC/SWIFT-kode',
    },
    street: {
        validator: (value?: SearchAddressResponse | string, { required }: { required: boolean} = { required: false}) => {
            if (!value)
                return required ? 'Indast en gyldig adresse' : true;

            const street = (typeof value === 'string' ? value : value.street) || '';
            if (!street?.length)
                return required ? 'Indast en gyldig adresse' : true;

            if (street.length <= 2)
                return 'Indast en gyldig adresse';
            
            if (!(/\d/.test(street))) {
                return 'Addressen skal indeholde et husnummer';
            }
    
            return true;
        },
        label: 'Indast en gyldig adresse',
    },
    city: {
        validator: (value?: PostalCodeInformation | string, { required }: { required: boolean} = { required: false}) => {
            if (!value)
                return required ? 'Indast en gyldig by' : true;

            const city = (typeof value === 'string' ? value : value.name) || '';
            if (!city?.length)
                return required ? 'Indast en gyldig by' : true;

            if (city.length < 2)
                return 'Indast en gyldig by';
    
            return true;
        },
        label: 'Indast en gyldig by',
    },
    postalCode: {
        validator: (value?: PostalCodeInformation | string | number, { required }: { required: boolean} = { required: false}) => {
            if (!value)
                return required ? 'Indast et gyldigt postnummer' : true;

            const code = (typeof value === 'object' ? value.number : value)?.toString() ?? '';
            if (!code.length)
                return required ? 'Indast et gyldigt postnummer' : true;

            if (code.length < 2)
                return 'Indast et gyldigt postnummer';
            
            return true;
        },
        label: 'Indast et gyldigt postnummer',
    },
    min: {
        validator: min,
        label: getLabel('Min'),
    },
    max: {
        validator: max,
        label: getLabel('Max'),
    },
    guid: {
        validator: (value, [target], ctx) => {
            if (!value || typeof value !== 'string') {
                return true;
            }

            if (!isValidGuid(value)) {
                return 'Indtast et gyldig ID';
            }

            return true;
        },
        label: 'Indtast et gyldigt ID',
    },
    alpha_spaces: {
        validator: (value, [target], ctx) => {
            if (value === null || value === undefined || value === '') {
                return true;
            }

            return /[a-zA-Z -]+$/.test(value);
        },
        label: getLabel('AlphabeticalWithWhitespace'),
    },
    alpha_num_spaces: {
        validator: (value, [target], ctx) => {
            if (value === null || value === undefined || value === '') {
                return true;
            }

            return /^[a-zA-Z0-9](?:[a-zA-Z0-9 ]*[a-zA-Z0-9])?$/.test(value);
        },
        label: getLabel('AlphaNumericalWithWhitespace'),
    },
    'is-not': {
        validator: is_not,
        label: getLabel('IsNot'),
    },
    'min-value': {
        validator: min_value,
        label: getLabel('MinValue'),
    },
    'max-value': {
        validator: max_value,
        label: getLabel('MaxValue'),
    },
    between: {
        validator: between,
        label: getLabel('Between'),
    },
    url: {
        validator: url,
        label: getLabel('Url'),
    },
    cvr: {
        validator: (value, [target], ctx) => {
            if (!value || typeof value !== 'string') {
                return true;
            }

            if (value.length !== 8) {
                return 'CVR Skal være præcist 8 tegn';
            }

            if (!(/^\d+$/.test(value))) {
                return 'CVR må kun indeholde tal';
            }

            return true;
        },
        label: getLabel('Cvr'),
    },
    address: {
        validator: (value, [target], ctx) => {
            if (!value || typeof value !== 'string') {
                return true;
            }

            if (!(/\d/.test(value))) {
                return 'Addressen skal indeholde et husnummer';
            }

            return true;
        },
        label: getLabel('Address'),
    },
    phone: {
        validator: (phoneNumber, [target], ctx) => {
            if (!phoneNumber || typeof phoneNumber !== 'string') {
                return true;
            }
            
            return isValidPhoneNumber(phoneNumber);
        },
        label: getLabel('Phone'),
    },
    ipAddress: {
        validator: (value, [target], ctx) => {
            if (!value || typeof value !== 'string') {
                return true;
            }

            if (!isValidIpAddress(value)) {
                return 'Indtast en gyldig IP Addresse';
            }

            return true;
        },
        label: getLabel('IpAddress'),
    },
    numeric: {
        validator: numeric,
        label: getLabel('Numeric'),
    },
    confirmed: {
        validator: confirmed,
        label: getLabel('Confirmed'),
    },
    decimal: {
        validator: (value, { decimals = '*', separator = decimalSeperator } = {}) => {
            if (value === null || value === undefined || value === '') {
                return true;
            }

            if (Number(decimals) === 0) {
                return /^-?\d*$/.test(value);
            }

            const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`;
            const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`);

            return regex.test(value);
        },
        label: getLabel('Decimal'),
    },
    password: {
        validator: (value, [target], ctx) => {
            function hasLength(value: string): boolean {
                return value.length >= 8;
            }

            function hasUppercase(value: string): boolean {
                return !!(value.match(/[A-Z]/)?.length);
            }

            function hasLowercase(value: string): boolean {
                return !!(value.match(/[a-z]/)?.length);
            }

            function hasNumeric(value: string): boolean {
                return !!(value.match(/\d/)?.length);
            }

            if (!value) return false;
            if (!hasLength(value)) return false;
            if (!hasLowercase(value)) return false;
            if (!hasUppercase(value)) return false;
            if (!hasNumeric(value)) return false;

            return true;
        },
        label: getLabel('Password'),
    },
};

configure({
    generateMessage,
});

setupRules();

function getLabel(rule: string): string {
    return `Validation.${rule}`;
}

function setupRules(): void {
    Object.keys(rules).forEach((id: string) => {
        const validator = rules[id].validator;
        defineRule(id, validator);
    });
}

function generateMessage(context: FieldContext): string {
    if (!context.rule) {
        throw new Error(`Validation-error but no rules: ${context.field}`);
    }

    const labelKey = rules[context.rule.name].label;
    const params: string[] = [];
    if (context.rule.params) {
        // Can be array or object with key/values - e.g. { min: xx, max: yy }. Convert to array always.
        params.push(...Array.isArray(context.rule.params) ? context.rule.params : Object.values(context.rule.params));
    }
    return translate(labelKey, context.field, params);
}

function translate(label: string, field: string, params: string[]): string {
    return dictionary.get(label, ...[field, ...params]);
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
defineRule('confirmed', (value, [target], ctx) => {
    if (value === ctx.form[target]) {
        return true;
    }
    return dictionary.get('Validation.Confirmed');
});
